import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useReducer,
  Dispatch,
  useEffect,
} from 'react';
import { useAcademicsMaterialsHook } from '../hooks/useAcademicsMaterialsHook';
import { useAcademicsFiltersContext } from './academicsFilters.context';
import {
  IAcademicsMaterials,
  MaterialCategory,
  MaterialStatus,
  MaterialCondition,
  TAcademicMaterial,
} from '../../../../apis/academics/academicsMaterials.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { UseQueryResult } from '@tanstack/react-query';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { IAcademicsMaterialsResponse } from '../../../../apis/academics/academics.interfaces';

type TQueryError = IFHError<{ message: string }>;

export enum ACTION_TYPE {
  SET_IS_MANAGE_MATERIAL_OPEN = 'SET_IS_MANAGE_MATERIAL_OPEN',
  SET_SELECTED_MATERIAL = 'SET_SELECTED_MATERIAL',
  SET_SELECTED_MATERIAL_ID = 'SET_SELECTED_MATERIAL_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_MATERIAL_LOADING = 'SET_IS_MATERIAL_LOADING',
  SET_IS_MATERIAL_ERROR = 'SET_IS_MATERIAL_ERROR',
  SET_MATERIAL_ERROR_MESSAGE = 'SET_MATERIAL_ERROR_MESSAGE',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_ACADEMICS_MATERIALS = 'UPDATE_ACADEMICS_MATERIALS',
  UPDATE_MATERIALS_LIST = 'UPDATE_MATERIALS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MANAGE_EACH_MATERIAL_OPEN = 'SET_IS_MANAGE_EACH_MATERIAL_OPEN',
  SET_SELECTED_EACH_MATERIAL = 'SET_SELECTED_EACH_MATERIAL',
  SET_SELECTED_EACH_MATERIAL_ID = 'SET_SELECTED_EACH_MATERIAL_ID',
  SET_SELECTED_MATERIAL_GROUP_ID = 'SET_SELECTED_MATERIAL_GROUP_ID',
  SET_IS_CREATE_EACH_MATERIAL_MODE = 'SET_IS_CREATE_EACH_MATERIAL_MODE',
  SET_IS_EDIT_EACH_MATERIAL_MODE = 'SET_IS_EDIT_EACH_MATERIAL_MODE',
  SET_IS_EACH_MATERIAL_LOADING = 'SET_IS_EACH_MATERIAL_LOADING',
  SET_IS_EACH_MATERIAL_ERROR = 'SET_IS_EACH_MATERIAL_ERROR',
  SET_EACH_MATERIAL_ERROR_MESSAGE = 'SET_EACH_MATERIAL_ERROR_MESSAGE',
}

type TAcademicsMaterialsAppState = {
  isManageMaterialOpen: boolean;
  isMaterialLoading: boolean;
  isMaterialError: boolean;
  materialErrorMessage: string;
  selectedMaterial: IAcademicsMaterials | null;
  selectedMaterialId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
  materialsList: IAcademicsMaterials[];
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  isManageEachMaterialOpen: boolean;
  isEachMaterialLoading: boolean;
  isEachMaterialError: boolean;
  eachMaterialErrorMessage: string;
  selectedEachMaterial: TAcademicMaterial | null;
  selectedEachMaterialId: string | null;
  selectedMaterialGroupId: string | null;
  isCreateEachMaterialMode: boolean;
  isEditEachMaterialMode: boolean;
};

type Action =
  | { type: ACTION_TYPE.SET_IS_MANAGE_MATERIAL_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_MATERIAL_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_MATERIAL_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_MATERIAL_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_MATERIAL;
      payload: IAcademicsMaterials | null;
    }
  | { type: ACTION_TYPE.SET_SELECTED_MATERIAL_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
      type: ACTION_TYPE.UPDATE_ACADEMICS_MATERIALS;
      payload: IAcademicsMaterials[];
    }
  | { type: ACTION_TYPE.UPDATE_MATERIALS_LIST; payload: IAcademicsMaterials[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_IS_MANAGE_EACH_MATERIAL_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EACH_MATERIAL_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EACH_MATERIAL_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_EACH_MATERIAL_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_EACH_MATERIAL;
      payload: TAcademicMaterial | null;
    }
  | { type: ACTION_TYPE.SET_SELECTED_EACH_MATERIAL_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_MATERIAL_GROUP_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_EACH_MATERIAL_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_EACH_MATERIAL_MODE; payload: boolean };

const initialAcademicsMaterialsState: TAcademicsMaterialsAppState = {
  isManageMaterialOpen: false,
  isMaterialLoading: false,
  isMaterialError: false,
  materialErrorMessage: '',
  selectedMaterial: null,
  selectedMaterialId: null,
  isCreateMode: false,
  isEditMode: false,
  materialsList: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  isManageEachMaterialOpen: false,
  isEachMaterialLoading: false,
  isEachMaterialError: false,
  eachMaterialErrorMessage: '',
  selectedEachMaterial: null,
  selectedEachMaterialId: null,
  selectedMaterialGroupId: null,
  isCreateEachMaterialMode: false,
  isEditEachMaterialMode: false,
};

const academicsMaterialsReducer = (
  state: TAcademicsMaterialsAppState,
  action: Action
): TAcademicsMaterialsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        userParams: { ...state.userParams, ...action.payload },
      };
    case ACTION_TYPE.SET_IS_MANAGE_MATERIAL_OPEN:
      return { ...state, isManageMaterialOpen: action.payload };
    case ACTION_TYPE.SET_IS_MATERIAL_LOADING:
      return { ...state, isMaterialLoading: action.payload };
    case ACTION_TYPE.SET_IS_MATERIAL_ERROR:
      return { ...state, isMaterialError: action.payload };
    case ACTION_TYPE.SET_MATERIAL_ERROR_MESSAGE:
      return { ...state, materialErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_MATERIAL:
      return { ...state, selectedMaterial: action.payload };
    case ACTION_TYPE.SET_SELECTED_MATERIAL_ID:
      return { ...state, selectedMaterialId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    case ACTION_TYPE.UPDATE_ACADEMICS_MATERIALS:
      return { ...state, materialsList: action.payload };
    case ACTION_TYPE.UPDATE_MATERIALS_LIST:
      return { ...state, materialsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MANAGE_EACH_MATERIAL_OPEN:
      return { ...state, isManageEachMaterialOpen: action.payload };
    case ACTION_TYPE.SET_IS_EACH_MATERIAL_LOADING:
      return { ...state, isEachMaterialLoading: action.payload };
    case ACTION_TYPE.SET_IS_EACH_MATERIAL_ERROR:
      return { ...state, isEachMaterialError: action.payload };
    case ACTION_TYPE.SET_EACH_MATERIAL_ERROR_MESSAGE:
      return { ...state, eachMaterialErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_EACH_MATERIAL:
      return { ...state, selectedEachMaterial: action.payload };
    case ACTION_TYPE.SET_SELECTED_EACH_MATERIAL_ID:
      return { ...state, selectedEachMaterialId: action.payload };
    case ACTION_TYPE.SET_SELECTED_MATERIAL_GROUP_ID:
      return { ...state, selectedMaterialGroupId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_EACH_MATERIAL_MODE:
      return { ...state, isCreateEachMaterialMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_EACH_MATERIAL_MODE:
      return { ...state, isEditEachMaterialMode: action.payload };
    default:
      return state;
  }
};

interface TAcademicsMaterialsState {
  // Queries
  materialsQuery: UseQueryResult<IAcademicsMaterialsResponse, TQueryError>;

  // Refetch functions
  refetchMaterials: () => void;

  // Material utility functions
  getMaterialById: (id: string) => IAcademicsMaterials | undefined;
  getMaterialsByCategory: (category: MaterialCategory) => TAcademicMaterial[];
  getMaterialsByStatus: (status: MaterialStatus) => TAcademicMaterial[];
  getMaterialsByCondition: (
    condition: MaterialCondition
  ) => TAcademicMaterial[];
  getMaterialsByDepartment: (department: string) => IAcademicsMaterials[];
  getMaterialsByLocation: (location: string) => IAcademicsMaterials[];
  getMaterialsBySupplier: (supplier: string) => TAcademicMaterial[];
  getMaterialsByTag: (tag: string) => TAcademicMaterial[];
  getMaterialsNeedingMaintenance: () => TAcademicMaterial[];
  getMaterialsNeedingReorder: () => TAcademicMaterial[];
  getMaterialsByDateRange: (
    startDate: Date,
    endDate: Date
  ) => TAcademicMaterial[];
  getMaterialsByCostRange: (
    minCost: number,
    maxCost: number
  ) => TAcademicMaterial[];
  getMaterialsBySearchTerm: (searchTerm: string) => TAcademicMaterial[];

  // Inventory management functions
  getTotalInventoryValue: () => number;
  getDepartmentInventorySummary: () => {
    department: string;
    totalValue: number;
    totalCount: number;
  }[];
  getCategoryInventorySummary: () => {
    category: MaterialCategory;
    totalValue: number;
    totalCount: number;
  }[];
  getStatusInventorySummary: () => {
    status: MaterialStatus;
    totalValue: number;
    totalCount: number;
  }[];
  getMaterialsExpiringSoon: (daysThreshold: number) => TAcademicMaterial[];

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

type TAcademicsMaterialsContextState = {
  academicsMaterialsAppState: TAcademicsMaterialsAppState;
  academicsMaterialRecords: IAcademicsMaterials[];
};

interface AcademicsMaterialsProviderProps {
  children: ReactNode;
  paginationParams?: TPaginationParams;
}

const AcademicsMaterialsContext = createContext<
  TAcademicsMaterialsState | undefined
>(undefined);

const AcademicsMaterialsAppStateContext = createContext<
  TAcademicsMaterialsContextState | undefined
>(undefined);

const AcademicsMaterialsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const DEFAULT_PAGINATION: TPaginationParams = { page: 1, limit: 10 };

export const AcademicsMaterialsProvider: React.FC<
  AcademicsMaterialsProviderProps
> = ({ children, paginationParams = DEFAULT_PAGINATION }) => {
  const { academicYear, grade } = useAcademicsFiltersContext();
  const materialsParams = {
    ...paginationParams,
    ...(academicYear && { academicYear }),
    ...(grade && { grade }),
  };
  const materialsQuery = useAcademicsMaterialsHook(materialsParams);
  const [appState, dispatch] = useReducer(academicsMaterialsReducer, {
    ...initialAcademicsMaterialsState,
    userParams: paginationParams,
    materialsList: materialsQuery.data?.materials || [],
  });

  const refetchMaterials = useCallback(() => {
    materialsQuery.refetch();
  }, [materialsQuery]);

  // Material utility functions
  const getMaterialById = useCallback(
    (id: string): IAcademicsMaterials | undefined => {
      return materialsQuery.data?.materials.find(
        (material: IAcademicsMaterials) => material.id === id
      );
    },
    [materialsQuery]
  );

  const getMaterialsByCategory = useCallback(
    (category: MaterialCategory): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) => m.category === category
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsByStatus = useCallback(
    (status: MaterialStatus): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) => m.status === status
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsByCondition = useCallback(
    (condition: MaterialCondition): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) => m.condition === condition
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsByDepartment = useCallback(
    (department: string): IAcademicsMaterials[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.filter(
        (material: IAcademicsMaterials) => material.department === department
      );
    },
    [materialsQuery]
  );

  const getMaterialsByLocation = useCallback(
    (location: string): IAcademicsMaterials[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.filter(
        (material: IAcademicsMaterials) => material.location === location
      );
    },
    [materialsQuery]
  );

  const getMaterialsBySupplier = useCallback(
    (supplier: string): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) => m.supplier === supplier
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsByTag = useCallback(
    (tag: string): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter((m: TAcademicMaterial) =>
            m.tags.includes(tag)
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsNeedingMaintenance =
    useCallback((): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      const now = new Date();
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) =>
              m.type === 'lab_equipment' &&
              'maintenanceSchedule' in m &&
              m.maintenanceSchedule?.nextMaintenance &&
              m.maintenanceSchedule.nextMaintenance <= now
          )
      );
    }, [materialsQuery]);

  const getMaterialsNeedingReorder = useCallback((): TAcademicMaterial[] => {
    if (!materialsQuery.data) return [];
    return materialsQuery.data.materials.flatMap(
      (material: IAcademicsMaterials) =>
        material.materials.filter(
          (m: TAcademicMaterial) => m.quantity <= m.minimumQuantity
        )
    );
  }, [materialsQuery]);

  const getMaterialsByDateRange = useCallback(
    (startDate: Date, endDate: Date): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) =>
              m.purchaseDate >= startDate && m.purchaseDate <= endDate
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsByCostRange = useCallback(
    (minCost: number, maxCost: number): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) => m.cost >= minCost && m.cost <= maxCost
          )
      );
    },
    [materialsQuery]
  );

  const getMaterialsBySearchTerm = useCallback(
    (searchTerm: string): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      const term = searchTerm.toLowerCase();
      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) =>
              m.name.toLowerCase().includes(term) ||
              m.description.toLowerCase().includes(term) ||
              m.tags.some((tag) => tag.toLowerCase().includes(term))
          )
      );
    },
    [materialsQuery]
  );

  // Inventory management functions
  const getTotalInventoryValue = useCallback((): number => {
    if (!materialsQuery.data) return 0;
    return materialsQuery.data.materials.reduce(
      (total, material: IAcademicsMaterials) => total + material.totalValue,
      0
    );
  }, [materialsQuery]);

  const getDepartmentInventorySummary = useCallback((): {
    department: string;
    totalValue: number;
    totalCount: number;
  }[] => {
    if (!materialsQuery.data) return [];
    const summary = new Map<
      string,
      { totalValue: number; totalCount: number }
    >();

    materialsQuery.data.materials.forEach((material: IAcademicsMaterials) => {
      const current = summary.get(material.department) || {
        totalValue: 0,
        totalCount: 0,
      };
      summary.set(material.department, {
        totalValue: current.totalValue + material.totalValue,
        totalCount: current.totalCount + material.totalCount,
      });
    });

    return Array.from(summary.entries()).map(([department, data]) => ({
      department,
      ...data,
    }));
  }, [materialsQuery]);

  const getCategoryInventorySummary = useCallback((): {
    category: MaterialCategory;
    totalValue: number;
    totalCount: number;
  }[] => {
    if (!materialsQuery.data) return [];
    const summary = new Map<
      MaterialCategory,
      { totalValue: number; totalCount: number }
    >();

    materialsQuery.data.materials.forEach((material: IAcademicsMaterials) => {
      material.materials.forEach((m: TAcademicMaterial) => {
        const current = summary.get(m.category) || {
          totalValue: 0,
          totalCount: 0,
        };
        summary.set(m.category, {
          totalValue: current.totalValue + m.cost * m.quantity,
          totalCount: current.totalCount + m.quantity,
        });
      });
    });

    return Array.from(summary.entries()).map(([category, data]) => ({
      category,
      ...data,
    }));
  }, [materialsQuery]);

  const getStatusInventorySummary = useCallback((): {
    status: MaterialStatus;
    totalValue: number;
    totalCount: number;
  }[] => {
    if (!materialsQuery.data) return [];
    const summary = new Map<
      MaterialStatus,
      { totalValue: number; totalCount: number }
    >();

    materialsQuery.data.materials.forEach((material: IAcademicsMaterials) => {
      material.materials.forEach((m: TAcademicMaterial) => {
        const current = summary.get(m.status) || {
          totalValue: 0,
          totalCount: 0,
        };
        summary.set(m.status, {
          totalValue: current.totalValue + m.cost * m.quantity,
          totalCount: current.totalCount + m.quantity,
        });
      });
    });

    return Array.from(summary.entries()).map(([status, data]) => ({
      status,
      ...data,
    }));
  }, [materialsQuery]);

  const getMaterialsExpiringSoon = useCallback(
    (daysThreshold: number): TAcademicMaterial[] => {
      if (!materialsQuery.data) return [];
      const now = new Date();
      const thresholdDate = new Date(
        now.getTime() + daysThreshold * 24 * 60 * 60 * 1000
      );

      return materialsQuery.data.materials.flatMap(
        (material: IAcademicsMaterials) =>
          material.materials.filter(
            (m: TAcademicMaterial) =>
              (m.type === 'digital_resource' &&
                'expiryDate' in m &&
                m.expiryDate &&
                m.expiryDate <= thresholdDate) ||
              (m.type === 'stationery' &&
                'expiryDate' in m &&
                m.expiryDate &&
                m.expiryDate <= thresholdDate)
          )
      );
    },
    [materialsQuery]
  );

  // Loading and error states
  const isLoading = materialsQuery.isLoading;
  const isError = materialsQuery.isError;
  const error = materialsQuery.error || null;

  // Sync materialsList with query data
  useEffect(() => {
    if (materialsQuery.data?.materials) {
      dispatch({
        type: ACTION_TYPE.UPDATE_MATERIALS_LIST,
        payload: materialsQuery.data.materials,
      });
    }
  }, [materialsQuery.data?.materials]);

  const value = useMemo(
    () => ({
      // Queries
      materialsQuery,

      // Refetch functions
      refetchMaterials,

      // Material utility functions
      getMaterialById,
      getMaterialsByCategory,
      getMaterialsByStatus,
      getMaterialsByCondition,
      getMaterialsByDepartment,
      getMaterialsByLocation,
      getMaterialsBySupplier,
      getMaterialsByTag,
      getMaterialsNeedingMaintenance,
      getMaterialsNeedingReorder,
      getMaterialsByDateRange,
      getMaterialsByCostRange,
      getMaterialsBySearchTerm,

      // Inventory management functions
      getTotalInventoryValue,
      getDepartmentInventorySummary,
      getCategoryInventorySummary,
      getStatusInventorySummary,
      getMaterialsExpiringSoon,

      // Loading and error states
      isLoading,
      isError,
      error,
    }),
    [
      materialsQuery,
      refetchMaterials,
      getMaterialById,
      getMaterialsByCategory,
      getMaterialsByStatus,
      getMaterialsByCondition,
      getMaterialsByDepartment,
      getMaterialsByLocation,
      getMaterialsBySupplier,
      getMaterialsByTag,
      getMaterialsNeedingMaintenance,
      getMaterialsNeedingReorder,
      getMaterialsByDateRange,
      getMaterialsByCostRange,
      getMaterialsBySearchTerm,
      getTotalInventoryValue,
      getDepartmentInventorySummary,
      getCategoryInventorySummary,
      getStatusInventorySummary,
      getMaterialsExpiringSoon,
      isLoading,
      isError,
      error,
    ]
  );

  const appStateValue: TAcademicsMaterialsContextState = useMemo(
    () => ({
      academicsMaterialsAppState: appState,
      academicsMaterialRecords: appState.materialsList,
    }),
    [appState]
  );

  return (
    <AcademicsMaterialsContext.Provider value={value}>
      <AcademicsMaterialsAppStateContext.Provider value={appStateValue}>
        <AcademicsMaterialsDispatchContext.Provider value={dispatch}>
          {children}
        </AcademicsMaterialsDispatchContext.Provider>
      </AcademicsMaterialsAppStateContext.Provider>
    </AcademicsMaterialsContext.Provider>
  );
};

export const useAcademicsMaterialsContext = (): TAcademicsMaterialsState => {
  const context = useContext(AcademicsMaterialsContext);

  if (context === undefined) {
    throw new Error(
      'useAcademicsMaterialsContext must be used within a AcademicsMaterialsProvider'
    );
  }

  return context;
};

export const useAcademicsMaterialsState =
  (): TAcademicsMaterialsContextState => {
    const context = useContext(AcademicsMaterialsAppStateContext);
    if (context === undefined) {
      throw new Error(
        'useAcademicsMaterialsState must be used within a AcademicsMaterialsProvider'
      );
    }
    return context;
  };

export const useAcademicsMaterialsDispatch = (): Dispatch<Action> => {
  const context = useContext(AcademicsMaterialsDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsMaterialsDispatch must be used within a AcademicsMaterialsProvider'
    );
  }
  return context;
};

export const useAcademicsMaterialsActions = () => {
  const dispatch = useAcademicsMaterialsDispatch();
  if (dispatch === undefined) {
    throw new Error(
      'useAcademicsMaterialsActions must be used within a AcademicsMaterialsProvider'
    );
  }

  const setIsManageMaterialOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_MATERIAL_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedMaterial = (material: IAcademicsMaterials | null) => {
    dispatch?.({ type: ACTION_TYPE.SET_SELECTED_MATERIAL, payload: material });
  };

  const setIsMaterialLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MATERIAL_LOADING,
      payload: isLoading,
    });
  };

  const setIsMaterialError = (isError: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_MATERIAL_ERROR, payload: isError });
  };

  const setMaterialErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_MATERIAL_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedMaterialId = (materialId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_MATERIAL_ID,
      payload: materialId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch?.({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const setIsManageEachMaterialOpen = (isOpen: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_MANAGE_EACH_MATERIAL_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedEachMaterial = (material: TAcademicMaterial | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_EACH_MATERIAL,
      payload: material,
    });
  };

  const setIsEachMaterialLoading = (isLoading: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_EACH_MATERIAL_LOADING,
      payload: isLoading,
    });
  };

  const setIsEachMaterialError = (isError: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_EACH_MATERIAL_ERROR,
      payload: isError,
    });
  };

  const setEachMaterialErrorMessage = (errorMessage: string) => {
    dispatch?.({
      type: ACTION_TYPE.SET_EACH_MATERIAL_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedEachMaterialId = (materialId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_EACH_MATERIAL_ID,
      payload: materialId,
    });
  };

  const setSelectedMaterialGroupId = (groupId: string | null) => {
    dispatch?.({
      type: ACTION_TYPE.SET_SELECTED_MATERIAL_GROUP_ID,
      payload: groupId,
    });
  };

  const setIsCreateEachMaterialMode = (isCreateMode: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_CREATE_EACH_MATERIAL_MODE,
      payload: isCreateMode,
    });
  };

  const setIsEditEachMaterialMode = (isEditMode: boolean) => {
    dispatch?.({
      type: ACTION_TYPE.SET_IS_EDIT_EACH_MATERIAL_MODE,
      payload: isEditMode,
    });
  };

  return {
    setIsManageMaterialOpen,
    setSelectedMaterial,
    setIsMaterialLoading,
    setIsMaterialError,
    setMaterialErrorMessage,
    setSelectedMaterialId,
    setIsCreateMode,
    setIsEditMode,
    setIsManageEachMaterialOpen,
    setSelectedEachMaterial,
    setIsEachMaterialLoading,
    setIsEachMaterialError,
    setEachMaterialErrorMessage,
    setSelectedEachMaterialId,
    setSelectedMaterialGroupId,
    setIsCreateEachMaterialMode,
    setIsEditEachMaterialMode,
  };
};
